import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { useResponsive } from "src/utils/useResponsive"
import LegalDocument from "./LegalDocument"

export type LegalDocumentI = "GENERAL_TERMS" | "PRIVACY_POLICY_REQUESTERS"

const Footer = () => {
  const { t } = useTranslation("profile")
  const { isMobile } = useResponsive()
  const [legalDocumentType, setLegalDocumentType] = useState<LegalDocumentI>()
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false)

  const handleLegalDocumentOpen = (type: LegalDocumentI) => {
    if (isMobile) {
      // On mobile, download the PDF directly
      const link = document.createElement("a")
      link.href = `/api/wfl/documents/${type}`
      link.download = `${type}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      // On desktop, open in dialog
      setLegalDocumentType(type)
    }
  }

  const handleLegalDocumentClose = () => {
    setLegalDocumentType(undefined)
  }

  const handleDeleteAccountOpen = () => {
    setDeleteAccountDialogOpen(true)
  }

  const handleDeleteAccountClose = () => {
    setDeleteAccountDialogOpen(false)
  }

  const handleDeleteAccountConfirm = () => {
    window.location.href = "https://exfluency.com/delete-account/"
  }

  return (
    <>
      <Dialog
        open={!!legalDocumentType}
        onClose={handleLegalDocumentClose}
        PaperProps={{
          style: {
            maxWidth: isMobile ? "95vw" : 1000,
            margin: isMobile ? "16px" : "auto"
          }
        }}
        fullScreen={isMobile}
      >
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {legalDocumentType && <LegalDocument legalDocument={legalDocumentType} />}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
          <Button variant="outlined" onClick={handleLegalDocumentClose}>
            {"Close"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteAccountDialogOpen}
        onClose={handleDeleteAccountClose}
        PaperProps={{
          sx: {
            maxWidth: isMobile ? "95vw" : 500,
            margin: isMobile ? "16px" : "auto",
            padding: 1,
            paddingTop: 4
          }
        }}
      >
        <DialogTitle variant="h4" sx={{ p: { xs: 2, md: 3 } }}>{t("deleteAccount.confirmTitle")}</DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography>{t("deleteAccount.confirmMessage1")}</Typography>
          <br />
          <Typography>{t("deleteAccount.confirmMessage2")}</Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 2, md: 3 },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1, md: 0 }
          }}
        >
          <Button variant="contained" onClick={handleDeleteAccountConfirm} fullWidth={isMobile}>
            {t("deleteAccount.confirm")}
          </Button>
          <Button variant="outlined" onClick={handleDeleteAccountClose} fullWidth={isMobile}>
            {t("deleteAccount.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <Stack spacing={1} sx={{ mt: { xs: 2, md: 3 } }}>
        <Button
          variant="text"
          onClick={() => handleLegalDocumentOpen("GENERAL_TERMS")}
          sx={{ fontSize: { xs: "14px", md: "16px" } }}
        >
          {t("legalDocument.GENERAL_TERMS")}
        </Button>
        <Button
          variant="text"
          onClick={() => handleLegalDocumentOpen("PRIVACY_POLICY_REQUESTERS")}
          sx={{ fontSize: { xs: "14px", md: "16px" } }}
        >
          {t("legalDocument.PRIVACY_POLICY_REQUESTERS")}
        </Button>
        <Button variant="text" onClick={handleDeleteAccountOpen} sx={{ fontSize: { xs: "14px", md: "16px" } }}>
          {t("deleteAccount.button")}
        </Button>
      </Stack>
    </>
  )
}

export default Footer
